import React, { useEffect, useMemo, useState, useCallback } from 'react'
import BigNumber from 'bignumber.js'
import {
  useAccount,
  useContractRead,
  usePrepareContractWrite,
  useContractWrite,
  useWaitForTransaction,
} from 'wagmi'
import { parseUnits } from 'viem'
import { MaxUint256 } from 'ethers'
import { newTokenAdd, tokenAdd, swapAdd, chainId } from '../../config'
import erc20Abi from '../../abi/erc20.json'
import swapAbi from '../../abi/swap.json'
import { toast } from 'react-hot-toast'
import ConnectWallet from './../ConnectWallet'

const bn = (v) => new BigNumber(v || 0)
const fmt = (n) => bn(n).toFormat()

const SwapCard = ({ buttonColor = '#00f6ff' }) => {
  const { address } = useAccount()
  const [amount, setAmount] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const oldBalRead = useContractRead({
    address: tokenAdd,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const newBalRead = useContractRead({
    address: newTokenAdd,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const allowanceRead = useContractRead({
    address: tokenAdd,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [address, swapAdd],
    enabled: !!address,
    chainId,
    watch: true,
  })

  const liquidityRead = useContractRead({
    address: newTokenAdd,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [swapAdd],
    chainId,
    watch: true,
  })

  const rateRead = useContractRead({
    address: swapAdd,
    abi: swapAbi,
    functionName: 'swapRate',
    chainId,
    watch: true,
  })

  const oldBal = useMemo(() => {
    const raw = oldBalRead.data ? bn(oldBalRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [oldBalRead.data])

  const newBal = useMemo(() => {
    const raw = newBalRead.data ? bn(newBalRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [newBalRead.data])

  const allowance = useMemo(
    () => (allowanceRead.data ? bn(allowanceRead.data.toString()) : bn(0)),
    [allowanceRead.data],
  )

  const allowanceHuman = useMemo(() => allowance.div(bn(10).pow(6)), [allowance])

  const liquidity = useMemo(() => {
    const raw = liquidityRead.data ? bn(liquidityRead.data.toString()) : bn(0)
    return raw.div(bn(10).pow(6))
  }, [liquidityRead.data])

  const rateWad = bn(rateRead.data ? rateRead.data.toString() : '1000000')
  const rateDisplay = rateWad.div(1e6).toFixed(6)
  const amountBN = bn(amount || 0)
  const previewReceive = amountBN.times(rateWad).div(1e6)

  useEffect(() => {
    if (!amount) return setErrorMessage('')
    if (amountBN.lte(0)) return setErrorMessage('Amount must be greater than zero.')
    if (amountBN.gt(oldBal)) return setErrorMessage('Insufficient balance.')
    if (previewReceive.gt(liquidity)) return setErrorMessage('Not enough liquidity in pool.')
    setErrorMessage('')
  }, [amount, amountBN, oldBal, previewReceive, liquidity])

  const { config: approveCfg } = usePrepareContractWrite({
    address: tokenAdd,
    abi: erc20Abi,
    functionName: 'approve',
    args: [swapAdd, MaxUint256],
    enabled: !!address && !errorMessage,
    chainId,
  })

  const { write: approveWrite, data: approveTx } = useContractWrite(approveCfg)
  const {
    isLoading: approving,
    isSuccess: approveSuccess,
    isError: approveError,
  } = useWaitForTransaction({ hash: approveTx?.hash })

  const { config: swapCfg } = usePrepareContractWrite({
    address: swapAdd,
    abi: swapAbi,
    functionName: 'swapToken',
    args: [parseUnits(amount, 6)],
    enabled: !!address && !!amount && !errorMessage,
    chainId,
  })
  const { write: swapWrite, data: swapTx } = useContractWrite(swapCfg)
  const {
    isLoading: swapping,
    isSuccess: swapSuccess,
    isError: swapError,
  } = useWaitForTransaction({ hash: swapTx?.hash })

  useEffect(() => {
    if (approveSuccess) {
      toast.success(
        <div className="text-center py-2">
          Approve Success!
          {approveTx?.hash && (
            <div>
              <a
                style={{ color: '#000' }}
                href={`https://polygonscan.com/tx/${approveTx.hash}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Polygonscan
              </a>
            </div>
          )}
        </div>,
      )
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [approveSuccess, approveTx?.hash])

  useEffect(() => {
    if (approveError) {
      toast.error(<div className="text-center py-2">Approval failed. Please try again.</div>)
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [approveError])

  useEffect(() => {
    if (swapSuccess) {
      toast.success(
        <div className="text-center py-2">
          Swap Success!
          {swapTx?.hash && (
            <div>
              <a
                style={{ color: '#000' }}
                href={`https://polygonscan.com/tx/${swapTx.hash}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Polygonscan
              </a>
            </div>
          )}
        </div>,
      )
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [swapSuccess, swapTx?.hash])

  useEffect(() => {
    if (swapError) {
      toast.error(<div className="text-center py-2">Swap failed. Please try again.</div>)
      const t = setTimeout(() => toast.dismiss(), 3000)
      return () => clearTimeout(t)
    }
  }, [swapError])

  return (
    <div className="staking-card-container">
      <div className="staking-card">
        <h4 className="total-stake">Swap Rate: </h4>
        <h3 className="amount">1 OLD IP ≈ {rateDisplay} NEW IP</h3>

        <div className="wallet-balance">Your OLD IP token balance: {fmt(oldBal)}</div>
        <div className="wallet-balance">Your NEW IP token balance: {fmt(newBal)}</div>

        <div className="stake-input-container" style={{ marginTop: 16 }}>
          <input
            type="number"
            placeholder="Enter amount to swap"
            className="stake-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
          />
          <button
            className="max-button"
            onClick={() => setAmount(oldBal.decimalPlaces(6, BigNumber.ROUND_DOWN).toString())}
          >
            MAX
          </button>
        </div>

        {errorMessage && <p style={{ color: '#ff0000', marginTop: 8 }}>{errorMessage}</p>}

        {!address ? (
          <div className="text-center mb-2" style={{ marginTop: 12 }}>
            <ConnectWallet />
          </div>
        ) : (
          <>
            {allowanceHuman.lt(amountBN) && (
              <button
                className="connect-button"
                onClick={() => approveWrite?.()}
                disabled={!address || !amount || !!errorMessage || approving}
                style={{ backgroundColor: buttonColor, marginTop: 12 }}
              >
                {approving ? 'Approving...' : 'Approve'}
              </button>
            )}

            {allowanceHuman.gte(amountBN) && (
              <button
                className="connect-button"
                onClick={() => swapWrite?.()}
                disabled={!address || !amount || !!errorMessage || swapping}
                style={{ backgroundColor: buttonColor, marginTop: 12 }}
              >
                {swapping ? 'Swapping...' : 'Swap'}
              </button>
            )}
          </>
        )}

        <button
          className="connect-button"
          onClick={async () => {
            try {
              if (window.ethereum) {
                await window.ethereum.request({
                  method: 'wallet_watchAsset',
                  params: {
                    type: 'ERC20',
                    options: {
                      address: newTokenAdd,
                      symbol: 'IP',
                      decimals: 6,
                      image: '',
                    },
                  },
                })
              } else {
                toast.error('Wallet is not available.')
              }
            } catch (err) {
              console.error(err)
              toast.error('Failed to add token to wallet.')
            }
          }}
          disabled={!address}
          style={{ backgroundColor: buttonColor, marginTop: 12 }}
        >
          Add New IP Token Wallet
        </button>

        <div className="staking-info mt-4">
          You’ll send {fmt(amount || 0)} OLD IP tokens and receive approximately{' '}
          {fmt(previewReceive)} NEW IP tokens.
        </div>
      </div>
    </div>
  )
}

export default SwapCard
